import { useEffect, useState } from "react";
import { Bell, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";


interface Lottery {
  id: string;
  slug: string;
  name: string;
}

interface Pref {
  lottery_id: string;
  prize_alerts_enabled: boolean;
  pool_alerts_enabled: boolean;
}

const AlertasContent = () => {
  const { user } = useAuth();
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [prefs, setPrefs] = useState<Record<string, Pref>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [lRes, pRes] = await Promise.all([
        supabase.from("lotteries").select("id, slug, name").order("name"),
        supabase.from("email_preferences").select("lottery_id, prize_alerts_enabled, pool_alerts_enabled"),
      ]);
      if (lRes.data) setLotteries(lRes.data as Lottery[]);
      const map: Record<string, Pref> = {};
      (pRes.data ?? []).forEach((p: any) => {
        map[p.lottery_id] = p;
      });
      setPrefs(map);
      setLoading(false);
    };
    if (user) load();
  }, [user]);

  const update = async (lotteryId: string, field: "prize_alerts_enabled" | "pool_alerts_enabled", value: boolean) => {
    if (!user) return;
    const key = `${lotteryId}-${field}`;
    setSaving(key);

    const current = prefs[lotteryId] ?? { lottery_id: lotteryId, prize_alerts_enabled: true, pool_alerts_enabled: true };
    const next = { ...current, [field]: value };

    const { error } = await supabase
      .from("email_preferences")
      .upsert(
        { user_id: user.id, lottery_id: lotteryId, prize_alerts_enabled: next.prize_alerts_enabled, pool_alerts_enabled: next.pool_alerts_enabled },
        { onConflict: "user_id,lottery_id" },
      );

    setSaving(null);
    if (error) {
      toast.error("Não foi possível salvar");
      return;
    }
    setPrefs((prev) => ({ ...prev, [lotteryId]: next }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-foreground">Alertas por e-mail</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Escolha em quais loterias você quer receber e-mails quando seus jogos ou bolões forem premiados.
        </p>
      </div>

      <Card className="border-lt-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-serif text-xl">
            <Bell className="h-4 w-4 text-primary" />
            Preferências por loteria
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : (
            <div className="divide-y divide-lt-border">
              <div className="hidden grid-cols-[1fr_auto_auto] items-center gap-6 pb-3 text-[11px] uppercase tracking-wider text-muted-foreground sm:grid">
                <span>Loteria</span>
                <span className="w-24 text-center">Jogos</span>
                <span className="w-24 text-center">Bolões</span>
              </div>
              {lotteries.map((l) => {
                const p = prefs[l.id];
                const prize = p?.prize_alerts_enabled ?? true;
                const pool = p?.pool_alerts_enabled ?? true;
                return (
                  <div key={l.id} className="grid grid-cols-1 items-center gap-3 py-4 sm:grid-cols-[1fr_auto_auto] sm:gap-6">
                    <div>
                      <div className="text-sm font-medium text-foreground">{l.name}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{l.slug}</div>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:w-24 sm:justify-center">
                      <span className="text-xs text-muted-foreground sm:hidden">Jogos</span>
                      <div className="flex items-center gap-2">
                        {saving === `${l.id}-prize_alerts_enabled` && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                        <Switch
                          checked={prize}
                          onCheckedChange={(v) => update(l.id, "prize_alerts_enabled", v)}
                          disabled={!!saving}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:w-24 sm:justify-center">
                      <span className="text-xs text-muted-foreground sm:hidden">Bolões</span>
                      <div className="flex items-center gap-2">
                        {saving === `${l.id}-pool_alerts_enabled` && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                        <Switch
                          checked={pool}
                          onCheckedChange={(v) => update(l.id, "pool_alerts_enabled", v)}
                          disabled={!!saving}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-lt-border bg-lt-bg2">
        <CardContent className="flex items-start gap-3 py-5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
            <Mail className="h-4 w-4 text-primary" />
          </div>
          <div className="text-sm leading-relaxed text-muted-foreground">
            <strong className="text-foreground">Como funciona:</strong> enviamos e-mail só quando seu jogo ou bolão atinge uma faixa de prêmio
            (Mega 4+, Lotofácil 11+, Quina 2+, Dupla 3+). Você pode desativar a qualquer momento por loteria.
          </div>
        </CardContent>
      </Card>

      <Card className="border-lt-border bg-card">
        <CardContent className="flex items-center gap-3 py-4 text-xs text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-accent" />
          E-mails são enviados para <span className="font-mono text-foreground">{user?.email}</span>.
        </CardContent>
      </Card>
    </div>
  );
};

export default AlertasContent;
